import React, { useState, useEffect, useCallback } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { ArrowLeftIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useKeycloak } from '@react-keycloak/web';
import type { RateSchedule, CreateRateScheduleDto, RateType } from '../types/billing.types';

const API_BASE = import.meta.env.VITE_API_URL || '';

const RATE_TYPES: { value: RateType; label: string }[] = [
  { value: 'standard', label: 'Standard' },
  { value: 'discounted', label: 'Discounted' },
  { value: 'pro_bono', label: 'Pro Bono' },
  { value: 'blended', label: 'Blended' },
];

const RateSchedulesPage: React.FC = () => {
  const navigate = useAnimatedNavigate();
  const { keycloak } = useKeycloak();
  const [schedules, setSchedules] = useState<RateSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [filterRole, setFilterRole] = useState('');
  const [filterActive, setFilterActive] = useState('true');

  // Form state
  const [form, setForm] = useState<CreateRateScheduleDto>({
    hourly_rate: 0,
    effective_date: new Date().toISOString().split('T')[0],
    rate_type: 'standard',
    priority: 0,
  });

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchSchedules = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterRole) params.set('role', filterRole);
      if (filterActive) params.set('is_active', filterActive);
      const res = await fetch(`${API_BASE}/api/v1/billing/rate-schedules?${params}`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) setSchedules(json.data);
      else setError(json.message || 'Failed to fetch rate schedules');
    } catch {
      setError('Failed to fetch rate schedules');
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [getHeaders, filterRole, filterActive]);

  useEffect(() => { fetchSchedules(); }, [fetchSchedules]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const url = editingId
        ? `${API_BASE}/api/v1/billing/rate-schedules/${editingId}`
        : `${API_BASE}/api/v1/billing/rate-schedules`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: getHeaders(),
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Save failed');
      setShowForm(false);
      setEditingId(null);
      await fetchSchedules();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (schedule: RateSchedule) => {
    setEditingId(schedule.rate_schedule_id);
    setForm({
      user_id: schedule.user_id || undefined,
      client_id: schedule.client_id || undefined,
      matter_id: schedule.matter_id || undefined,
      role: schedule.role || undefined,
      hourly_rate: Number(schedule.hourly_rate),
      effective_date: schedule.effective_date?.split('T')[0] || '',
      end_date: schedule.end_date?.split('T')[0] || undefined,
      rate_type: schedule.rate_type,
      description: schedule.description || undefined,
      is_active: schedule.is_active,
      priority: schedule.priority,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this rate schedule?')) return;
    try {
      await fetch(`${API_BASE}/api/v1/billing/rate-schedules/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      await fetchSchedules();
    } catch {
      setError('Failed to delete rate schedule');
    }
  };

  const handleNew = () => {
    setEditingId(null);
    setForm({
      hourly_rate: 0,
      effective_date: new Date().toISOString().split('T')[0],
      rate_type: 'standard',
      priority: 0,
    });
    setShowForm(true);
  };

  const formatCurrency = (val: number | string) => `$${Number(val).toFixed(2)}`;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 lex-tab-panel">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/billing')}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Rate Schedules</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage billing rates by attorney, client, matter, or role.
            </p>
          </div>
        </div>
        <button
          onClick={handleNew}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          Add Rate
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="">All Roles</option>
          <option value="Attorney">Attorney</option>
          <option value="Paralegal">Paralegal</option>
          <option value="Admin">Admin</option>
        </select>
        <select
          value={filterActive}
          onChange={(e) => setFilterActive(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
        >
          <option value="true">Active Only</option>
          <option value="false">Inactive Only</option>
          <option value="">All</option>
        </select>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 space-y-4"
        >
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {editingId ? 'Edit Rate Schedule' : 'New Rate Schedule'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
              <select
                value={form.role || ''}
                onChange={(e) => setForm(p => ({ ...p, role: e.target.value || undefined }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="">— Any —</option>
                <option value="Attorney">Attorney</option>
                <option value="Paralegal">Paralegal</option>
                <option value="Admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hourly Rate ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.hourly_rate}
                onChange={(e) => setForm(p => ({ ...p, hourly_rate: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Effective Date</label>
              <input
                type="date"
                required
                value={form.effective_date}
                onChange={(e) => setForm(p => ({ ...p, effective_date: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
              <input
                type="date"
                value={form.end_date || ''}
                onChange={(e) => setForm(p => ({ ...p, end_date: e.target.value || undefined }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rate Type</label>
              <select
                value={form.rate_type || 'standard'}
                onChange={(e) => setForm(p => ({ ...p, rate_type: e.target.value as RateType }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                {RATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <input
                type="number"
                min="0"
                value={form.priority || 0}
                onChange={(e) => setForm(p => ({ ...p, priority: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <input
                type="text"
                value={form.description || ''}
                onChange={(e) => setForm(p => ({ ...p, description: e.target.value || undefined }))}
                placeholder="e.g. Standard attorney rate for 2026"
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); setEditingId(null); }}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50"
            >
              {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}

      {/* Table */}
      {!initialLoadDone ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-lex-accent border-t-transparent rounded-full" />
        </div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <p>No rate schedules found.</p>
          <button onClick={handleNew} className="mt-2 text-lex-accent text-sm hover:underline">
            Create your first rate schedule
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Attorney / Role</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Rate</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Type</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Effective</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">End</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Scope</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Priority</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {schedules.map((s) => (
                <tr key={s.rate_schedule_id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 text-gray-900 dark:text-white">
                    {s.user ? `${s.user.first_name} ${s.user.last_name}` : s.role || '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-white">
                    {formatCurrency(s.hourly_rate)}/hr
                  </td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400 capitalize">{s.rate_type}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{s.effective_date?.split('T')[0]}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{s.end_date?.split('T')[0] || 'Ongoing'}</td>
                  <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                    {s.matter ? `Matter: ${s.matter.matter_number}` : s.client_id ? 'Client-specific' : 'Firm-wide'}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-400">{s.priority}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${s.is_active
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {s.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => handleEdit(s)}
                        className="p-1 text-gray-400 hover:text-lex-accent rounded"
                        title="Edit"
                      >
                        <PencilSquareIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(s.rate_schedule_id)}
                        className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RateSchedulesPage;
